import { TableClient, AzureNamedKeyCredential } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import {
  TABLE,
  PK,
  RK,
  type InstalledStamp,
  stampToEntity,
  entityToStamp,
} from "@m8t-stack/platform-release";
import { runAz } from "./az.js";
import { LocalCliError } from "./errors.js";
import { getCallerObjectId } from "./auth.js";
import { grantStorageTableDataContributor } from "./rbac.js";
import { discoverStampStorage } from "./platform-storage-discovery.js";

export { stampToEntity, entityToStamp };
export type { InstalledStamp, ComponentState, ImageStampEntry, PersonaStampEntry } from "@m8t-stack/platform-release";

/** Resolve a shared-key `TableClient` for the stamp table (Owner can always list keys). */
async function makeSharedKeyClient(opts: {
  tableEndpoint: string;
  accountResourceId: string;
  accountName: string;
}): Promise<TableClient | null> {
  // `az storage account keys list` requires --account-name + -g (it rejects --ids).
  const rg = /\/resourceGroups\/([^/]+)\//i.exec(opts.accountResourceId)?.[1];
  if (!rg) return null;
  const key = (
    await runAz(["storage", "account", "keys", "list", "--account-name", opts.accountName, "-g", rg, "--query", "[0].value", "-o", "tsv"])
  ).trim();
  if (!key) return null;
  return new TableClient(opts.tableEndpoint, TABLE, new AzureNamedKeyCredential(opts.accountName, key));
}

/**
 * Write the platform stamp via a 4-step auth ladder. The probe IS the real
 * write (`upsertEntity`) — `createTable()` succeeding is not evidence of
 * entity-write access (a founder who is subscription Owner but lacks
 * "Storage Table Data Contributor" can create/see a table that already
 * exists, then 403 on the actual upsert).
 *
 *   1. AAD write — try the founder's own `az login` credential directly.
 *      F04's MI driver has the data-plane role, so this succeeds for it.
 *   2. Shared-key fallback — on a 403, fall back to the account key (fast +
 *      reliable; an Owner can always list keys). One-line progress warning.
 *   3. Self-grant + bounded backoff — only if shared-key is unavailable
 *      (`allowSharedKeyAccess:false`): grant the caller Storage Table Data
 *      Contributor at the account scope and retry the AAD write a few times
 *      (data-plane role propagation takes minutes).
 *   4. Still failing → throw `PLATFORM_STAMP_NO_ACCESS`.
 *
 * A non-403 error at step 1 rethrows immediately — it's a real failure, not
 * part of the access-denied path this ladder exists to handle.
 */
export async function writeStamp(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  stamp: InstalledStamp;
  onProgress?: (m: string) => void;
}): Promise<void> {
  const { tableEndpoint, accountResourceId, accountName } = await discoverStampStorage(opts);
  // stampToEntity's return type is deliberately the wire-shape `Record<string, unknown>`
  // (this is the contract downstream tasks import); it always carries partitionKey/rowKey
  // at runtime, so this narrows the type for the SDK's `TableEntity<T>` constraint.
  const entity = stampToEntity(opts.stamp) as Record<string, unknown> & { partitionKey: string; rowKey: string };

  // 1. AAD write.
  const aad = new TableClient(tableEndpoint, TABLE, opts.credential);
  await aad.createTable().catch(() => undefined); // best-effort ensure; not a proof of write-access
  try {
    await aad.upsertEntity(entity, "Replace");
    return;
  } catch (e) {
    if (!is403(e)) throw e;
  }

  // 2. Shared-key fallback (fast + reliable — Owner can always list keys).
  opts.onProgress?.("writing the platform stamp with the account key — your identity lacks the Storage Table data role.");
  const shared = await makeSharedKeyClient({ tableEndpoint, accountResourceId, accountName });
  if (shared) {
    await shared.createTable().catch(() => undefined);
    await shared.upsertEntity(entity, "Replace");
    return;
  }

  // 3. Self-grant (last resort — shared-key is disabled). A grant failure (no
  //    OID, or the caller lacks role-assignment rights) is terminal → the
  //    actionable PLATFORM_STAMP_NO_ACCESS below, with the underlying cause.
  opts.onProgress?.("shared-key access is disabled — granting Storage Table Data Contributor to your identity (one-time)…");
  try {
    const oid = await getCallerObjectId();
    await grantStorageTableDataContributor({
      credential: opts.credential,
      subscriptionId: opts.subscriptionId,
      scope: accountResourceId,
      principalId: oid,
    });
  } catch (e) {
    throw new LocalCliError({
      code: "PLATFORM_STAMP_NO_ACCESS",
      message: `Cannot write the platform stamp: AAD lacks the Storage Table data role for ${accountName}, shared-key access is disabled, and self-granting the role failed.`,
      hint: "Grant yourself 'Storage Table Data Contributor' on the storage account and retry.",
      cause: e,
    });
  }

  // Grant succeeded — retry the write while the data-plane role propagates
  // (minutes). A non-403 error here is a genuine write failure → propagate it.
  for (let i = 0; i < 6; i++) {
    await sleep(10_000);
    try {
      await aad.upsertEntity(entity, "Replace");
      return;
    } catch (e) {
      if (!is403(e)) throw e;
    }
  }

  // 4. Granted, but the role has not propagated in time.
  throw new LocalCliError({
    code: "PLATFORM_STAMP_NO_ACCESS",
    message: `Cannot write the platform stamp: granted 'Storage Table Data Contributor' on ${accountName}, but the data-plane role has not propagated yet.`,
    hint: "The role assignment persists — retry 'm8t platform update' in a few minutes.",
  });
}

/**
 * Read the platform stamp. Must stay robust for a founder who hasn't been
 * granted the data-plane role: a 403 on the AAD read falls back to
 * shared-key so `status`/`update` don't perpetually see "unmanaged" and
 * re-adopt. Any other failure (404, or the shared-key fallback erroring
 * too) is treated as "no stamp" — this is a best-effort read.
 */
export async function readStamp(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
}): Promise<InstalledStamp | null> {
  const { tableEndpoint, accountResourceId, accountName } = await discoverStampStorage(opts);
  const aad = new TableClient(tableEndpoint, TABLE, opts.credential);
  try {
    const row = await aad.getEntity(PK, RK);
    return entityToStamp(row);
  } catch (e) {
    if (is404(e)) return null;
    if (!is403(e)) return null;
  }

  try {
    const shared = await makeSharedKeyClient({ tableEndpoint, accountResourceId, accountName });
    if (!shared) return null;
    const row = await shared.getEntity(PK, RK);
    return entityToStamp(row);
  } catch {
    return null;
  }
}

function is403(e: unknown): boolean {
  return (e as { statusCode?: number }).statusCode === 403;
}
function is404(e: unknown): boolean {
  return (e as { statusCode?: number }).statusCode === 404;
}
function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
